import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { synchronizeBiwoCutFees } from './biwo-cart-fees.js';

// The drawer and cart page can coexist. A cart mutation must run only once.
let cartMutationPending = false;
let biwoFeeCheckStarted = false;
let biwoFeeSyncFailed = false;

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    const hasCutFees = this.querySelector('[data-biwo-role="fee"]') ||
      [...this.querySelectorAll('[data-biwo-fee-per-unit]')].some((row) => Number(row.dataset.biwoFeePerUnit) > 0);
    if (!biwoFeeCheckStarted && hasCutFees) {
      biwoFeeCheckStarted = true;
      queueMicrotask(() => this.retryCartSync(true));
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target) || cartMutationPending) return;
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.#getLineRow(line);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    if (cartMutationPending) return;
    const row = this.#getLineRow(line);
    if (row?.dataset.biwoRole === 'fee' && row.dataset.biwoConfig) return;
    // Keep configured rows visible until plates and their service lines agree.
    if (row?.dataset.biwoConfig) return this.updateQuantity({ line, quantity: 0, action: 'clear' });
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.#getLineRow(line);

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  async updateQuantity(config) {
    if (cartMutationPending) return;
    const { line, quantity } = config;
    const row = this.#getLineRow(line);
    if (!row || (row.dataset.biwoConfig && row.dataset.biwoRole === 'fee')) return;
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    cartMutationPending = true;
    this.#disableCartItems();
    const { cartTotal } = this.refs;
    cartTotal?.shimmer();
    const needsFeeSafety = Boolean(row.dataset.biwoConfig || document.querySelector('[data-biwo-role="fee"]'));
    let changedCart;
    try {
      changedCart = await this.#changeLine({ id: row.dataset.key, quantity });
      const cart = await synchronizeBiwoCutFees(changedCart, (change) => this.#changeLine(change));
      biwoFeeSyncFailed = false;
      this.#clearSyncErrors();
      await this.#applyCart(cart);
    } catch (error) {
      console.error(error);
      if (needsFeeSafety || biwoFeeSyncFailed) {
        // The first request may have succeeded. Block checkout until rechecked.
        biwoFeeSyncFailed = true;
        this.#showSyncError(error.message);
      } else {
        this.#handleCartError(line, { errors: error.message || 'Die Menge konnte nicht geändert werden. Bitte versuche es erneut.' });
      }
    } finally {
      resetShimmer(this);
      cartMutationPending = false;
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  #getLineRow(line) {
    return this.refs.cartItemRows?.find((row) => Number(row.dataset.cartLine) === Number(line));
  }

  #sectionRequest() {
    const sections = new Set([this.sectionId]);
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((item) => {
      sections.add(item.dataset.sectionId);
    });
    return { sections: [...sections].join(','), sections_url: window.location.pathname };
  }

  async #changeLine(change) {
    const body = JSON.stringify({ ...change, ...this.#sectionRequest() });
    const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
    const cart = await response.json();
    if (!response.ok || cart.errors || !Array.isArray(cart.items)) {
      const message = cart.description || cart.errors || 'Die Menge konnte nicht geändert werden. Bitte versuche es erneut.';
      throw new Error(typeof message === 'string' ? message : Object.values(message).join(' '));
    }
    return cart;
  }

  async #applyCart(cart) {
    this.#updateQuantitySelectors(cart);
    this.dispatchEvent(new CartUpdateEvent({}, this.sectionId, {
      itemCount: cart.item_count,
      source: 'cart-items-component',
      sections: cart.sections,
    }));
    const sectionHTML = cart.sections?.[this.sectionId];
    if (sectionHTML) await morphSection(this.sectionId, sectionHTML);
    else await sectionRenderer.renderSection(this.sectionId, { cache: false });
    this.#updateCartQuantitySelectorButtonStates();
  }

  async retryCartSync(initialCheck = false) {
    if (cartMutationPending) return;
    cartMutationPending = true;
    this.#disableCartItems();
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { cache: 'no-store' });
      if (!response.ok) throw new Error('Der Warenkorb konnte nicht geprüft werden. Bitte versuche es erneut.');
      const originalCart = await response.json();
      const cart = await synchronizeBiwoCutFees(originalCart, (change) => this.#changeLine(change));
      biwoFeeSyncFailed = false;
      this.#clearSyncErrors();
      if (!initialCheck || cart !== originalCart) await this.#applyCart(cart);
    } catch (error) {
      biwoFeeSyncFailed = true;
      this.#showSyncError(error.message);
    } finally {
      cartMutationPending = false;
      this.#enableCartItems();
    }
  }

  #showSyncError(message) {
    this.#clearSyncErrors();
    document.querySelectorAll('cart-items-component').forEach((component) => {
      const alert = document.createElement('div');
      alert.className = 'biwo-cart-sync-error';
      alert.setAttribute('role', 'alert');
      alert.append(document.createTextNode(message + ' Vor dem Bezahlen müssen die Zuschnittmengen geprüft werden.'));
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.textContent = 'Warenkorb erneut prüfen';
      retry.addEventListener('click', () => this.retryCartSync());
      alert.append(retry);
      component.prepend(alert);
    });
  }

  #clearSyncErrors() {
    document.querySelectorAll('.biwo-cart-sync-error').forEach((element) => element.remove());
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.#getLineRow(line)?.querySelector('cart-quantity-selector-component');
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) return;

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement) || !(cartItemErrorContainer instanceof HTMLElement)) return;

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    document.querySelectorAll('cart-items-component').forEach((component) => {
      component.classList.add('cart-items-disabled');
      component.inert = true;
    });
    document.querySelectorAll('.cart__ctas').forEach((ctas) => { ctas.inert = true; });
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    document.querySelectorAll('cart-items-component').forEach((component) => {
      component.classList.remove('cart-items-disabled');
      component.inert = false;
    });
    document.querySelectorAll('.cart__ctas').forEach((ctas) => { ctas.inert = biwoFeeSyncFailed; });
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    const totals = new Map();
    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      totals.set(variantId, (totals.get(variantId) || 0) + item.quantity);
    }
    // Different cuts of the same variant are distinct lines, but stock counts sum them.
    document.querySelectorAll('quantity-selector-component[data-variant-id], cart-quantity-selector-component[data-variant-id]').forEach((selector) => {
      if (!totals.has(selector.dataset.variantId)) totals.set(selector.dataset.variantId, 0);
    });
    for (const [variantId, quantity] of totals) {
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    const cartQuantitySelectors = document.querySelectorAll('cart-quantity-selector-component');
    for (const selector of cartQuantitySelectors) {
      if ('updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
        selector.updateButtonStates();
      }
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
